/**
 * Driver daily XP tick — pure helper extracted from hrSlice.addDailyXp.
 * 11/02/2026 (Lot N suite — refactor hrSlice >1000 lignes).
 *
 * Computes the daily XP gain for every driver and detects level-ups.
 *
 * XP gain rules (tuned 21/05/2026 from 2/5/8 → 3/6/12) :
 *   • active + assigned + vehicle has contract → +12 XP/day
 *   • active + assigned but vehicle idle       → +6 XP/day
 *   • idle / resting (no vehicle)              → +3 XP/day
 *   • training                                 → 0 XP (training grants its own bonus)
 *
 * Level-up side-effects (applied to the driver record) :
 *   • competence +0.2 (capped at 5)
 *   • accidentRisk -0.008 (floor 0.02)
 *   • employerCost ×1.04
 *   • satisfaction +5 (capped at 100)
 *   • xpToNext refreshed
 *
 * Pure : no `set()`, no `get()`. Returns the recomputed `updatedDrivers`
 * array + the `levelUps` list (used by the slice to build notifications
 * and roll the post-level-up raise message).
 *
 * @param {Object} state - Slice state (drivers, vehicles, totalDays).
 * @param {Object} thresholds - { getXpForLevel(level), getStartingXp(level) }
 * @returns {{ updatedDrivers: Array, levelUps: Array<{id,name,newLevel,driver}> }}
 */
export function runDailyXpTick(state, thresholds) {
  const { getXpForLevel, getStartingXp } = thresholds;
  const levelUps = [];
  const updatedDrivers = state.drivers.map(d => {
    if (d.status === 'training') return d;

    const baselineXp = getStartingXp(d.level || 1);
    const currentXp = Math.max(d.xp || 0, baselineXp);

    let xpGain = 3;
    if (d.status === 'active') {
      xpGain = 6;
      const vehicle = state.vehicles.find(v => v.assignedDriverId === d.id);
      if (vehicle?.assignedContractId) xpGain = 12;
    }

    const newXp = currentXp + xpGain;
    const currentLevel = d.level || 1;
    const xpNeeded = getXpForLevel(currentLevel);

    if (newXp >= xpNeeded && currentLevel < 10) {
      const newLevel = currentLevel + 1;
      levelUps.push({ id: d.id, name: d.name, newLevel, driver: d });
      return {
        ...d,
        xp: newXp,
        level: newLevel,
        xpToNext: getXpForLevel(newLevel),
        competence: Math.min(5, (d.competence || 2) + 0.2),
        accidentRisk: Math.max(0.02, (d.accidentRisk || 0.1) - 0.008),
        employerCost: Math.round(d.employerCost * 1.04),
        satisfaction: Math.min(100, d.satisfaction + 5),
      };
    }
    return { ...d, xp: newXp };
  });

  return { updatedDrivers, levelUps };
}

/**
 * Pure gate for the post-level-up raise message. Returns true if the
 * driver is currently allowed to ask for a raise (cooldowns + salary
 * cap satisfied), false otherwise.
 *
 * Cooldowns enforced (11/02/2026 — fix Amina récurrent) :
 *   • Hard floor 90 j between two consecutive requests
 *     (`lastRaiseRequestDay`).
 *   • Decision-based cooldown : 180 j (accepted) / 60 j (refused) on
 *     `lastRaiseDecisionDay` / `lastRaiseDecisionOutcome`.
 *   • Skip if current salary > 150 % of computed baseline.
 *
 * @param {Object} driver
 * @param {number} todayDay
 * @param {number} expectedSalary - 0 → skip salary check.
 * @returns {boolean} true → driver may ask; false → block.
 */
export function canDriverAskForRaiseAfterLevelup(driver, todayDay, expectedSalary) {
  // 25/07/2026 (choix user 3a) — fréquence « raisonnable » : plancher dur
  // 180 j entre deux demandes, tranquillité 360 j après hausse accordée,
  // 90 j après refus.
  const lastReq = driver.lastRaiseRequestDay || 0;
  if (todayDay - lastReq < 180) return false;
  const lastDec = driver.lastRaiseDecisionDay || 0;
  if (lastDec > 0) {
    const outcome = driver.lastRaiseDecisionOutcome;
    const cooldown = outcome === 'accepted' ? 360 : 90;
    if (todayDay - lastDec < cooldown) return false;
  }
  if (expectedSalary > 0 && (driver.salary || 0) > expectedSalary * 1.5) return false;
  return true;
}
